import { useCallback } from 'react';

import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
import usePropertyContent from '@/components/PackagePage/hooks/usePropertyContent';
import useTiers from '@/hooks/useTiers';
import { useFlightModal } from '@/v2/components/pages/PackageView/contexts/FlightModalContext';
import { useOfferModalControls } from '@/v2/components/pages/PackageView/contexts/OfferModalControlsContext';
import { useRoomOptionsData } from '@/v2/components/pages/PackageView/contexts/RoomOptionsContext';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import dataLayer from '@/v2/utils/dataLayer';
import getEcommerceDetailedItem from '@/v2/utils/dataLayer/eventDataHelpers/getEcommerceDetailedItemEventData';

const useHandleReserve = () => {
  const shouldUseNewEvents = useGA4Events();
  const { searchQuery } = useSearchQuery();
  const { paymentType } = useCustomerSelection();
  const {
    packageOptions,
    roomOptions,
    isPreSearch,
    selectedRoomOption,
    upgradeOffer,
  } = useRoomOptionsData() || {};
  const tiers = useTiers();
  const propertyContent = usePropertyContent();
  const procuredOffer = useProcuredOffer();

  const { openUpgradeModal } = useOfferModalControls();
  const { openFlightModal } = useFlightModal({
    roomId: selectedRoomOption?.roomType?.id,
  });

  return useCallback(async () => {
    if (shouldUseNewEvents) {
      const item = getEcommerceDetailedItem({
        price: Number(
          selectedRoomOption?.matchingOffer?.charges?.total?.amount,
        ),
        property: { propertyContent, procuredOffer },
        tiers,
        originCode: searchQuery?.originCode,
        paymentType,
        travellers: {
          adults: searchQuery?.adults ?? 2,
          children: searchQuery?.children ?? 0,
          infants: searchQuery?.infants ?? 0,
        },
        travelDateRange: {
          start: searchQuery?.departureDate,
          end: searchQuery?.returnDate,
        },
        availableOfferCount: (packageOptions ?? []).length + 1,
        availableRoomCount: (roomOptions?.main || []).filter(
          ({ matchingLeadInOffer, matchingOffer }) =>
            isPreSearch ? !!matchingLeadInOffer : !!matchingOffer,
        )?.length,
        leadInRoom: selectedRoomOption ?? (roomOptions?.main || [])[0],
        isPreSearch: false,
      });

      dataLayer.addToCartEvent({ item });
    }
    const isPoOffer = !!selectedRoomOption?.matchingOffer?.leadInOffer;
    if (isPoOffer && upgradeOffer) {
      openUpgradeModal();
    } else {
      await openFlightModal({
        offerId: selectedRoomOption?.matchingOffer?.hotelOffer?.id,
      });
    }
  }, [
    shouldUseNewEvents,
    selectedRoomOption,
    upgradeOffer,
    propertyContent,
    procuredOffer,
    tiers,
    searchQuery?.originCode,
    searchQuery?.adults,
    searchQuery?.children,
    searchQuery?.infants,
    searchQuery?.departureDate,
    searchQuery?.returnDate,
    paymentType,
    packageOptions,
    roomOptions?.main,
    isPreSearch,
    openUpgradeModal,
    openFlightModal,
  ]);
};

export default useHandleReserve;
